import "./_style.less";
import { VuiTitle } from "../../@vendor/components";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState, useMemo } from "react";
import {
  VuiContentLoading,
  VuiHelmet,
  VuiStep,
  VuiSteps,
} from "../../@vendor/components";
import {
  BillIcon,
  CartDoneStep,
  CartPaymentStep,
  TickIcon,
} from "../../components";
import { usePassportService } from "../../@vendor/utils";
import { CreateBookingResponse } from "../../models/Booking";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import { useCheckoutBookingAssessment } from "../booking-assessment/providers/checkout-booking-assessment.provider";

export interface OrderSummary {
  subTotal: number;
  total: number;
}

type StepType = "payment" | "done";

const StepsCurrent = {
  payment: 0,
  done: 1,
};

const Checkout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refetchUser } = usePassportService();
  const { bookingData: values, onChangeBookingData } =
    useCheckoutBookingAssessment();
  const [contentLoading, setContentLoading] = useState<boolean>(false);
  const [orderSummary, setOrderSummary] = useState<OrderSummary>({
    subTotal: 0,
    total: 0,
  });
  const [currentStep, setCurrentStep] = useState<StepType>("payment");
  const [bookingResponse, setBookingResponse] =
    useState<CreateBookingResponse | null>(null);

  useEffect(() => {
    if (currentStep === "done" || contentLoading) return;
    if (!values?.assessment || !values?.patient) {
      navigate("/", { replace: true });
    }
  }, [values, navigate, currentStep, contentLoading]);

  const guestBookingData = useMemo(() => {
    if (values?.assessment && values?.patient) {
      const isMultiSession = (values.assessmentSchedules?.length || 0) > 1;

      return {
        name: values.patient.name || "",
        email: values.patient.email || "",
        phone_number: String(_.get(values.patient.phone_number, "phone", "")),
        phone_country: String(_.get(values.patient.phone_number, "code", "62")),
        date_of_birth:
          typeof values.patient.date_of_birth === "string"
            ? values.patient.date_of_birth
            : values.patient.date_of_birth?.format?.("YYYY-MM-DD") || "",

        bookings: [
          {
            assessment_id: values.assessment?.id,
            session_amount: values.assessment?.has_schedule
              ? values.assessment?.number_of_sessions || 1
              : 0,
            price: values.assessment?.price || 0,
            is_package: isMultiSession,

            ...(values.assessment?.has_schedule && isMultiSession
              ? {
                  schedules: values.schedule_ids?.map(
                    (scheduleId: number, index: number) => ({
                      id: scheduleId,
                      duration:
                        values.assessmentSchedules?.[index]?.session_duration ||
                        60,
                    })
                  ),
                }
              : values.assessment?.has_schedule && !isMultiSession
              ? {
                  schedule_id: values.schedule_ids?.[0],
                  duration:
                    values.assessmentSchedules?.[0]?.session_duration || 60,
                }
              : {}),
          },
        ],
      };
    }
    return undefined;
  }, [values]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const loadData = useCallback(async () => {
    setContentLoading(true);
    try {
      // Set order summary for guest checkout
      if (values.assessment && values.patient) {
        setOrderSummary({
          subTotal: values.assessment.price,
          total: values.assessment.price,
        });
      }
      setContentLoading(false);
    } catch (error) {
      console.error("Error loading guest data:", error);
      setContentLoading(false);
    }
  }, [values]);

  const handleCheckoutPayment = useCallback(
    (data: CreateBookingResponse) => {
      setBookingResponse(data);
      setCurrentStep("done");
      refetchUser();
      scrollToTop();
    },
    [refetchUser]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    return () => {
      try {
        if (currentStep !== "done") {
          onChangeBookingData(null);
        }
      } catch (e) {
        // noop
      }
    };
  }, [currentStep, onChangeBookingData]);

  return (
    <>
      <div id="app-cart-page">
        <VuiHelmet title={t("common.text.checkout")} />

        <div className="container">
          <VuiTitle text={t("common.text.checkout")} />

          <div className="cart-steps-wrapper">
            <VuiSteps
              current={StepsCurrent[currentStep]}
              labelPlacement="vertical"
            >
              <VuiStep icon={<BillIcon />} title={t("common.text.payment")} />
              <VuiStep icon={<TickIcon />} title={t("common.text.done")} />
            </VuiSteps>
          </div>

          {contentLoading ? (
            <VuiContentLoading loading={contentLoading} />
          ) : (
            <div className="cart-content-wrapper">
              <div className="cart-content">
                {currentStep === "payment" && (
                  <CartPaymentStep
                    orderSummary={orderSummary}
                    checkedCart={[]}
                    onCheckout={handleCheckoutPayment}
                    isGuestMode={true}
                    guestBookingData={guestBookingData}
                  />
                )}

                {currentStep === "done" && (
                  <CartDoneStep isGuestMode={true} data={bookingResponse} />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Checkout;
